import { Box } from "@mui/material";
import {
  hasPermission,
  PULSE_SOURCES,
  type Permission,
  type PulseQueues,
  type QueueRow,
  type Role,
} from "@xop/dashboard-shared";
import type { ReactElement } from "react";

import { PulseCard, PulseCardMessage, PulseRows, slug, type SystemRef } from "./PulseCard.js";
import { LicencesWatchCard, ReleasesWatchCard, ResellerApplicationsWatchCard } from "./WatchCards.js";

/**
 * "What should you look at right now", laid out as THREE columns (2026-09-15):
 *
 *   Customer care        — SLA breaches · New tickets · Recent Otto chats
 *   Revenue & pipeline   — Invoices due · Leads pending review · Overdue quotes
 *   Products & partners  — Licences expiring · Reseller applications · Latest releases
 *
 * ONE strict grid, not three stacks (2026-09-15): above `THREE_COLUMNS` the
 * column headings share the first row and every card row is `1fr`, so EVERY
 * card is exactly as tall as the tallest card on the page and the rows line up
 * across columns (cards pin their footer to the bottom — see `PulseCard`). The
 * sections use `display: contents` so their cards are direct grid items while
 * the DOM stays grouped by column. Between the two thresholds the headings span
 * the full width and cards flow two-up, each row as tall as its tallest card;
 * narrower than that everything stacks.
 *
 * THE NINE CARDS HOLD THEIR SHAPE AS LONG AS THEY STAY READABLE (2026-09-16).
 * The thresholds below are deliberately NOT the MUI breakpoints: `lg` (1200px)
 * dropped to two columns on any laptop with a browser sidebar open, which is
 * where this page is actually read. A card degrades gracefully — every row
 * ellipsises its label and keeps its meta — so the board holds three columns
 * down to 820px (≈250px a card) and two down to 560px, and the gaps and card
 * padding tighten on the way down so the width goes to content rather than to
 * whitespace.
 *
 * EVERY card is gated by the grant that opens the page it summarises, so a
 * role only sees the cards for systems it can actually reach, and a column
 * with no visible card is not rendered at all (the grid closes up):
 *   - Support cards    → `support:view`
 *   - Invoices due     → `crm:view` + `crm:financials` (the financial wall)
 *   - Leads            → `sales:view`
 *   - Overdue quotes   → `crm:view` + `crm:financials` (the financial wall)
 *   - Licences         → `crm:view` + `crm:licences`
 *   - Reseller apps    → `crm:view` + `crm:reseller_applications`
 *   - Releases         → `crm:view` + `crm:products_pricing`
 * The last three are super_admin-only in the baseline bundle. ("Drafts in
 * review" left the page on 2026-09-15; its `marketing_review` queue stays in
 * the payload because it drives a notification.) Inside the
 * Licences card, "awaiting your approval" is narrower still: approving is a
 * super_admin act with no grant, so it keys on the ROLE.
 *
 * The server is the real gate in every case — `pulse.ts` returns empty data
 * for un-permitted queues, and the watch cards' BFF endpoints refuse a
 * non-holder — so this is defence in depth plus a surface with no empty shells.
 *
 * Each queue caps at 5 rows server-side; if there are zero rows we show
 * a positive sentence rather than an empty box — UNLESS that queue's
 * upstream errored this tick (its `source` is in `upstreamErrors`), in
 * which case we say we couldn't reach the system instead of a
 * falsely-reassuring "all clear". A down crm-api must never read as
 * "No overdue invoices." while invoices are in fact overdue.
 */
/** Where the board changes shape. Raw widths, not MUI breakpoints — see the
 *  module note: the point is to keep all nine cards for as long as a card is
 *  still readable, and the MUI steps give up far too early for this page. */
export const THREE_COLUMNS = "@media (min-width:820px)";
export const TWO_COLUMNS = "@media (min-width:560px)";

export function NeedsAttention({
  queues,
  upstreamErrors = [],
  permissions,
  role,
}: {
  queues: PulseQueues;
  /** Non-fatal per-source upstream errors from the pulse payload
   * (`DashboardPulse.upstream_errors`). A queue whose source is here is
   * empty "for a reason" — render a warning, not the green all-clear. */
  upstreamErrors?: ReadonlyArray<{ source: string; message: string }>;
  /** The viewer's granted permissions — each card's gate. */
  permissions: readonly Permission[];
  /** The viewer's role. Only decides the super_admin-only licence approval
   * callout; everything else keys on permissions. */
  role?: Role | undefined;
}): JSX.Element {
  // Coalesce each queue to `[]` so a payload-shape skew (e.g. older
  // server still returning the pre-redesign 3-queue shape during a
  // rolling deploy) renders an empty state instead of crashing.
  const safe = {
    sla_breaches: queues.sla_breaches ?? [],
    new_tickets: queues.new_tickets ?? [],
    invoices_due: queues.invoices_due ?? [],
    leads_pending: queues.leads_pending ?? [],
    quotes_overdue: queues.quotes_overdue ?? [],
    recent_conversations: queues.recent_conversations ?? [],
  };
  // Which upstreams failed this tick — a queue mapped to one of these
  // is empty because we couldn't load it, not because it's all clear.
  const erroredSources = new Set(upstreamErrors.map((e) => e.source));
  const can = (p: Permission): boolean => hasPermission(permissions, p);
  const cards = (entries: ReadonlyArray<ReactElement | false>): ReactElement[] =>
    entries.filter((e): e is ReactElement => e !== false);

  const columns: Array<{ key: string; title: string; cards: ReactElement[] }> = [
    {
      key: "customer-care",
      title: "Customer care",
      cards: cards([
        can("support:view") && (
          <QueueSection
            key="sla"
            system={SUPPORT}
            title="SLA breaches"
            emptyCopy="No SLA breaches. Nice."
            rows={safe.sla_breaches}
            emptyTone="good"
            errored={erroredSources.has(PULSE_SOURCES.sla_breaches)}
            viewAllHref="/support/tickets?sla=at_risk"
          />
        ),
        can("support:view") && (
          <QueueSection
            key="new-tickets"
            system={SUPPORT}
            title="New tickets (last 24h)"
            emptyCopy="No new tickets in the last 24h."
            rows={safe.new_tickets}
            errored={erroredSources.has(PULSE_SOURCES.new_tickets)}
            viewAllHref="/support/tickets?sort=created_at&dir=desc"
          />
        ),
        can("support:view") && (
          <QueueSection
            key="otto"
            system={SUPPORT}
            title="Recent Otto chats"
            emptyCopy="No Otto conversations yet."
            rows={safe.recent_conversations}
            errored={erroredSources.has(PULSE_SOURCES.recent_conversations)}
            viewAllHref="/support/conversations"
          />
        ),
      ]),
    },
    {
      key: "revenue-pipeline",
      title: "Revenue & pipeline",
      cards: cards([
        // Behind the financial wall, NOT the coarse `crm:view` entry gate:
        // overdue-invoice rows carry the invoice's dollar amount and the card
        // deep-links to the `crm:financials`-guarded Invoices page. A CRM viewer
        // without financial access (e.g. a marketing_manager) sees no money
        // anywhere — so this card disappears for them too. The server already
        // returns an empty `invoices_due` for them (pulse.ts gates the same
        // way); this is the matching client gate.
        can("crm:view") && can("crm:financials") && (
          <QueueSection
            key="invoices"
            system={CRM}
            title="Invoices due"
            emptyCopy="No overdue invoices."
            rows={safe.invoices_due}
            emptyTone="good"
            errored={erroredSources.has(PULSE_SOURCES.invoices_due)}
            viewAllHref="/crm/invoices?status=overdue"
          />
        ),
        can("sales:view") && (
          <QueueSection
            key="leads"
            system={SALES}
            title="Leads pending review"
            emptyCopy="No leads waiting on you."
            rows={safe.leads_pending}
            errored={erroredSources.has(PULSE_SOURCES.leads_pending)}
            viewAllHref="/sales/leads"
          />
        ),
        // Replaced "Drafts in review" (2026-09-15). Same financial wall as
        // Invoices due — each row carries the quote total.
        can("crm:view") && can("crm:financials") && (
          <QueueSection
            key="quotes"
            system={CRM}
            title="Overdue quotes"
            emptyCopy="No quotes past their validity date."
            rows={safe.quotes_overdue}
            emptyTone="good"
            errored={erroredSources.has(PULSE_SOURCES.quotes_overdue)}
            viewAllHref="/crm/quotes"
          />
        ),
      ]),
    },
    {
      key: "products-partners",
      title: "Products & partners",
      cards: cards([
        can("crm:view") && can("crm:licences") && (
          <LicencesWatchCard key="licences" canApprove={role === "super_admin"} />
        ),
        can("crm:view") && can("crm:reseller_applications") && (
          <ResellerApplicationsWatchCard key="applications" />
        ),
        can("crm:view") && can("crm:products_pricing") && <ReleasesWatchCard key="releases" />,
      ]),
    },
  ].filter((column) => column.cards.length > 0);

  // Rows below the headings: as many as the fullest column has cards.
  const cardRows = Math.max(0, ...columns.map((column) => column.cards.length));
  const columnCount = Math.max(columns.length, 1);

  return (
    <Box
      data-testid="needs-attention"
      data-columns={columns.length}
      sx={{
        display: "grid",
        columnGap: { xs: 1.25, lg: 2 },
        rowGap: { xs: 1.25, lg: 1.5 },
        gridTemplateColumns: "minmax(0, 1fr)",
        gridAutoRows: "auto",
        [TWO_COLUMNS]: {
          gridTemplateColumns: `repeat(${Math.min(columnCount, 2)}, minmax(0, 1fr))`,
        },
        [THREE_COLUMNS]: {
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          // Headings row, then `cardRows` equal rows — `1fr` tracks in a grid of
          // indefinite height all resolve to the tallest card's height.
          gridTemplateRows: cardRows > 0 ? `auto repeat(${cardRows}, 1fr)` : "auto",
        },
      }}
    >
      {columns.map((column, columnIndex) => (
        <Box
          key={column.key}
          component="section"
          aria-labelledby={`pulse-column-${column.key}-title`}
          data-testid={`pulse-column-${column.key}`}
          sx={{ display: "contents" }}
        >
          <Box
            component="h2"
            id={`pulse-column-${column.key}-title`}
            sx={{
              m: 0,
              px: 0.5,
              alignSelf: "end",
              gridColumn: "1 / -1",
              // Space above a heading that starts a new group when stacked.
              mt: columnIndex === 0 ? 0 : 1.5,
              [THREE_COLUMNS]: { gridColumn: `${columnIndex + 1}`, gridRow: "1", mt: 0 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "text.secondary",
              "&::after": { content: '""', flex: 1, height: "1px", bgcolor: "divider" },
            }}
          >
            {column.title}
          </Box>
          {column.cards.map((card, rowIndex) => (
            <Box
              key={card.key ?? rowIndex}
              data-testid="pulse-grid-cell"
              sx={{
                minWidth: 0,
                [THREE_COLUMNS]: { gridColumn: `${columnIndex + 1}`, gridRow: `${rowIndex + 2}` },
              }}
            >
              {card}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/* ---------- system labels (kept here so every queue points at the same place) */

const SUPPORT: SystemRef = { label: "Support", href: "/support", systemKey: "support" };
const CRM: SystemRef = { label: "CRM", href: "/crm", systemKey: "crm" };
const SALES: SystemRef = { label: "Sales", href: "/sales", systemKey: "sales" };

/* ---------- a pulse queue as a card ------------------------------------- */

function QueueSection({
  system,
  title,
  rows,
  emptyCopy,
  emptyTone = "neutral",
  errored = false,
  viewAllHref,
}: {
  system: SystemRef;
  title: string;
  rows: QueueRow[];
  emptyCopy: string;
  emptyTone?: "good" | "neutral";
  /** This queue's upstream errored this tick — an empty list means
   * "couldn't load", not "all clear". Show a warning, not `emptyCopy`. */
  errored?: boolean;
  viewAllHref?: string;
}): JSX.Element {
  return (
    <PulseCard
      system={system}
      title={title}
      count={rows.length}
      testId={`queue-section-${slug(system.label)}-${slug(title)}`}
      footerHref={rows.length > 0 ? viewAllHref : undefined}
    >
      {rows.length === 0 ? (
        errored ? (
          // Empty because we couldn't reach the upstream this tick —
          // an amber "couldn't load", NOT the green all-clear. Pairs
          // with the page-level diagnostic <details> strip.
          <PulseCardMessage tone="warning" testId="queue-empty-error">
            Couldn&apos;t reach {system.label} — try again shortly.
          </PulseCardMessage>
        ) : (
          <PulseCardMessage tone={emptyTone}>{emptyCopy}</PulseCardMessage>
        )
      ) : (
        <PulseRows rows={rows} />
      )}
    </PulseCard>
  );
}
